// quick-260810-dqw: depth-agnostic tree traversal helpers.
//
// Extracted from Home.js so the "flat" tree (Site -> Shots/API, 3 levels) and the "classic"
// tree (Site -> pit -> RL -> shot -> plan -> dispatch, 5 levels) can share one implementation
// instead of Home.js hard-coding a fixed number of `.children.forEach` levels.
//
// sortTreeData also fixes the pre-existing "Created" sort bug: comparing `id` as a string put
// "subseq-10" before "subseq-9" (lexicographic '1' < '9'). It now parses the numeric suffix and
// compares numerically, falling back to the original string compare when either side doesn't
// parse.

/**
 * Recursively collects every node whose `id` starts with "dispatch-", at any depth.
 * Tolerates missing/null `children` at any level so it works against both the classic
 * 5-level tree and the flat 3-level tree unchanged.
 *
 * @param {Array} treeData
 * @returns {Array}
 */
export function collectDispatchNodes(treeData) {
    var nodes = [];

    function walk(list) {
        if (!list) return;
        for (var i = 0; i < list.length; i++) {
            var node = list[i];
            if (!node) continue;

            var id = String(node.id ?? "");
            if (id.startsWith("dispatch-")) {
                nodes.push(node);
            }

            if (node.children) {
                walk(node.children);
            }
        }
    }

    walk(treeData);
    return nodes;
}

/**
 * True when the tree is the flat Site -> Shots/API shape: the root's children contain a
 * synthetic "group-*" node (only TreeFlattener emits those; classic trees never do).
 *
 * @param {Array} treeData
 * @returns {boolean}
 */
export function isFlatTree(treeData) {
    var root = treeData?.[0];
    return !!root?.children?.some((child) => String(child?.id ?? "").startsWith("group-"));
}

/**
 * Ids to auto-expand on load in flat mode: the site root plus its direct children
 * (exactly the "Shots" group and, when present, the API subloc). Shots themselves stay
 * collapsed so plans/dispatches don't flood the view. Returns null for classic trees.
 *
 * @param {Array} treeData
 * @returns {Array|null}
 */
export function flatOpenIds(treeData) {
    if (!isFlatTree(treeData)) return null;
    var root = treeData[0];
    var ids = [root.id];
    root.children.forEach((child) => { ids.push(child.id); });
    return ids;
}

/**
 * In-place sort of a tree-node sibling array (and recursively its children).
 *
 * - sortType 'alpha': unchanged from the original Home.js behaviour (case-insensitive name compare).
 * - sortType 'created': numeric compare on the id's `-`-delimited numeric suffix (fixes
 *   "subseq-9" sorting after "subseq-10" under a plain string compare); falls back to the
 *   original string compare when either id doesn't parse as a number.
 * - Sibling arrays that contain a "group-*" id (the flat-mode "Shots"/"API" pins) are left in
 *   server order at that level (D-06) but still recursed into so their children get sorted.
 *
 * The comparator keeps the original two-way higher/lower shape (never returns 0) so ordering
 * of equal keys does not drift.
 *
 * @param {string} sortType 'alpha' | 'created'
 * @param {string} sortOrder 'asc' | 'desc'
 * @param {Array} data
 * @returns {Array} the same array, sorted in place
 */
export function sortTreeData(sortType, sortOrder, data) {
    var higher = 1; //a after b
    var lower = -1; //a before b
    if (sortOrder == 'desc') {
        higher = -1;
        lower = 1;
    }

    var hasPinnedGroup = data.some((n) => String(n?.id ?? "").startsWith("group-"));

    if (!hasPinnedGroup) {
        if (sortType == 'alpha') {
            data.sort((a, b) => a.name.toUpperCase() < b.name.toUpperCase() ? lower : higher); //'A' < 'B' in unicode compare
        } else if (sortType == 'created') {
            data.sort((a, b) => {
                var aNum = parseInt(String(a.id).split("-")[1], 10);
                var bNum = parseInt(String(b.id).split("-")[1], 10);
                if (!isNaN(aNum) && !isNaN(bNum)) {
                    return aNum < bNum ? lower : higher;
                }
                return a.id < b.id ? lower : higher;
            });
        }
    }

    for (var i = 0; i < data.length; i++) {
        if (data[i].children) {
            if (data[i].children.length > 0) {
                sortTreeData(sortType, sortOrder, data[i].children);
            }
        }
    }

    return data;
}

// quick-260810-jvd Task 3: pure helpers extracted from Home.js's shot-modal so the classic and
// flat "Add Shot" submit gates share one implementation instead of drifting (finding 1), and so
// the flat insert response can be parsed without ever mistaking the legacy `8888` sentinel — or
// any other malformed shape — for a real shot id (finding 2-frontend).

/**
 * Same character-set + no-op-rename validation Home.js has always applied to a shot name,
 * extracted so both the classic and flat submit gates can share it. Reuses the exact regex
 * already used at the classic "Add Shot" button's disabled expression.
 *
 * @param {string} name
 * @param {string} configName current/previous name (a no-op rename is treated as invalid)
 * @returns {boolean}
 */
export function isShotNameValid(name, configName) {
    if (name == null) return false;
    if (name === "") return false;
    if (!/^[0-9A-Za-z _-]+$/.test(name)) return false;
    if (name === configName) return false;
    return true;
}

/**
 * Whether the "Add/Rename Shot" submit action (button `disabled` + Enter key) should be
 * blocked. quick-260810-jvd finding 1: the classic gate includes `loadingsequence` (a pit/RL
 * "please wait" flag) — on a flat site (`configOption === 'subseqflat'`) there is no pit/RL
 * picker, so that flag never resolves and the classic gate deadlocks the Create button forever.
 * The flat gate drops the `loadingsequence` term; every other check (global loading, name
 * validity) still applies in both modes.
 *
 * @param {Object} args
 * @param {string} args.configOption 'subseq' (classic) | 'subseqflat' (flat)
 * @param {number} args.anyPageDataLoading
 * @param {boolean} args.loadingsequence
 * @param {string} args.createsubsequenceName
 * @param {string} args.configName
 * @returns {boolean}
 */
export function isAddShotSubmitBlocked({ configOption, anyPageDataLoading, loadingsequence, createsubsequenceName, configName }) {
    if (anyPageDataLoading > 0) return true;
    if (configOption !== "subseqflat" && loadingsequence) return true;
    if (!isShotNameValid(createsubsequenceName, configName)) return true;
    return false;
}

/**
 * Guards against ever treating a non-2xx / malformed / legacy-sentinel flat-insert response as
 * a successful shot creation (finding 2-frontend: the old code fed the raw response value
 * straight into `"subseq-" + subseqId`, so a `500` or the legacy `8888` sentinel became a
 * phantom `selectedDispatchTreeId`). Never throws.
 *
 * @param {number} status HTTP status code
 * @param {*} body parsed JSON response body (any shape, including null/8888/etc.)
 * @returns {{ok: true, subSequenceId: number, sequenceId: number}|{ok: false, error: string}}
 */
export function parseFlatInsertResponse(status, body) {
    try {
        if (status === 200 && body && typeof body === "object" && typeof body.subSequenceId === "number") {
            return { ok: true, subSequenceId: body.subSequenceId, sequenceId: body.sequenceId };
        }

        var error = (body && typeof body === "object" && typeof body.error === "string" && body.error)
            || ("Add Shot failed (status " + status + ")");

        return { ok: false, error };
    } catch (e) {
        return { ok: false, error: "Add Shot failed (unexpected response)" };
    }
}
